import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Camera,
  Search,
  ImageOff,
  CheckCircle2,
  Zap,
  Layers,
  Cog,
  Monitor,
  Boxes,
  MapPin,
  User as UserIcon,
  Tag,
} from "lucide-react";
import { resizeImage } from "@/lib/imageResize";

/**
 * "Quick Photos" — the go-around-the-warehouse-with-an-iPad page.
 *
 * One flat list of every Inventory item, Machine and Asset with a
 * per-row Camera button. Tap the button → device camera opens → snap →
 * photo is resized client-side, uploaded, and set as the item's PRIMARY
 * photo (prepended to `image_paths`). No dialogs, no scrolling forms.
 *
 * Optional "Batch" mode auto-scrolls to the next photoless row after
 * each save so the user can walk down the shelves tapping through 20
 * items in one session.
 */

const KINDS = [
  {
    id: "stock",
    label: "Inventory",
    icon: Boxes,
    listUrl: "/stock",
    putUrl: (id) => `/stock/${id}`,
    // Stock rows can carry a legacy `image_path` singleton — surface it
    // as `image_paths[0]` for the UI so old items don't look photoless.
    normalize: (r) => ({
      id: r.id,
      name: r.name,
      subtitle:
        [r.category, r.material, r.colour].filter(Boolean).join(" · ") ||
        r.supplier_default ||
        "",
      photos:
        r.image_paths?.length
          ? r.image_paths
          : r.image_path
            ? [r.image_path]
            : [],
    }),
  },
  {
    id: "machinery",
    label: "Machinery",
    icon: Cog,
    listUrl: "/machines",
    putUrl: (id) => `/machines/${id}`,
    normalize: (r) => ({
      id: r.id,
      name: r.name,
      subtitle:
        [r.category, r.manufacturer, r.model, r.department]
          .filter(Boolean)
          .join(" · ") || r.status || "",
      photos: r.image_paths || [],
    }),
  },
  {
    id: "assets",
    label: "Assets",
    icon: Monitor,
    listUrl: "/assets",
    putUrl: (id) => `/assets/${id}`,
    // Assets get a richer, chip-style meta so managers walking around
    // with an iPad can tell one Dell monitor from twelve others.
    // `ctx` is passed in from the page (users + departments) so we can
    // resolve `assignee_kind=user` ids → names without another lookup.
    normalize: (r, ctx = {}) => {
      const users = ctx.users || [];
      const departments = ctx.departments || [];
      let assignee = null;
      if (r.assignee_kind === "user") {
        const u = users.find((x) => x.id === r.assignee_id);
        assignee = u?.name || null;
      } else if (r.assignee_kind === "dept") {
        const d = departments.find((x) => x.id === r.assignee_id);
        assignee = d ? `${d.name} (dept)` : null;
      } else if (r.assignee_kind === "text") {
        assignee = r.assignee_text || null;
      }
      const brandModel = [r.brand, r.model].filter(Boolean).join(" ");
      return {
        id: r.id,
        name: r.name,
        subtitle:
          [r.category, r.serial].filter(Boolean).join(" · ") || r.status || "",
        meta: {
          brandModel: brandModel || null,
          location: r.location || null,
          assignee,
          assigneeMissing: !assignee,
          department: r.department || null,
        },
        photos: r.image_paths || [],
      };
    },
  },
];

const QuickPhotoRow = ({ row, kind, onSaved, isNextTarget }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);

  // Batch mode scrolls the next photoless row into view — feels like
  // the app is guiding the user through the walk-around.
  useEffect(() => {
    if (isNextTarget && scrollRef.current) {
      scrollRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [isNextTarget]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (file) await handle(file);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handle = async (file) => {
    setUploading(true);
    try {
      const resized = await resizeImage(file);
      const fd = new FormData();
      fd.append("file", resized);
      const { data: uploaded } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const nextPaths = [
        uploaded.path,
        ...row.photos.filter((p) => p !== uploaded.path),
      ];
      await api.put(kind.putUrl(row.id), { image_paths: nextPaths });
      toast.success(`Saved ${row.name}`);
      onSaved({ ...row, photos: nextPaths });
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const primary = row.photos?.[0];
  const missing = !primary;

  return (
    <div
      ref={scrollRef}
      data-testid={`quick-photo-row-${row.id}`}
      className={`flex items-center gap-3 py-3 px-3 border-2 border-black rounded-xl bg-white transition-colors ${
        isNextTarget ? "ring-4 ring-[#228EBF] ring-offset-2" : ""
      }`}
    >
      <div
        className={`w-16 h-16 shrink-0 rounded-lg border-2 border-black overflow-hidden flex items-center justify-center ${
          missing ? "bg-[#FCE4E4] text-[#8A1F1F]" : "bg-[#F0F4F8]"
        }`}
        data-testid={`quick-photo-thumb-${row.id}`}
      >
        {primary ? (
          <img
            src={imageUrl(primary)}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <ImageOff size={22} strokeWidth={2.5} />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm truncate">{row.name}</div>
        {row.meta?.brandModel && (
          <div className="text-[11px] font-bold text-[#001740] truncate flex items-center gap-1">
            <Tag size={10} strokeWidth={2.5} />
            {row.meta.brandModel}
          </div>
        )}
        {row.subtitle && (
          <div className="text-[11px] text-[#607087] truncate">
            {row.subtitle}
          </div>
        )}
        {(row.meta?.location || row.meta?.assignee || row.meta?.assigneeMissing) && (
          <div
            className="mt-1 flex flex-wrap gap-1"
            data-testid={`quick-photo-meta-${row.id}`}
          >
            {row.meta.location && (
              <span className="inline-flex items-center gap-1 border-2 border-black rounded-full px-2 py-0.5 text-[10px] font-bold bg-[#FFF7D6]">
                <MapPin size={10} strokeWidth={2.5} />
                {row.meta.location}
              </span>
            )}
            {row.meta.assignee ? (
              <span
                data-testid={`quick-photo-assignee-${row.id}`}
                className="inline-flex items-center gap-1 border-2 border-black rounded-full px-2 py-0.5 text-[10px] font-bold bg-[#DFEEF7]"
              >
                <UserIcon size={10} strokeWidth={2.5} />
                <span className="text-[#607087]">To:</span>
                {row.meta.assignee}
              </span>
            ) : (
              row.meta.assigneeMissing && (
                <span
                  data-testid={`quick-photo-assignee-missing-${row.id}`}
                  className="inline-flex items-center gap-1 border-2 border-black rounded-full px-2 py-0.5 text-[10px] font-bold bg-white text-[#8A1F1F]"
                >
                  <UserIcon size={10} strokeWidth={2.5} />
                  Unassigned
                </span>
              )
            )}
          </div>
        )}
        {missing ? (
          <div className="text-[10px] font-bold tracking-wider text-[#8A1F1F] mt-0.5">
            NO PHOTO
          </div>
        ) : (
          <div className="text-[10px] font-bold tracking-wider text-[#1F6B3A] mt-0.5 flex items-center gap-1">
            <CheckCircle2 size={11} strokeWidth={3} /> {row.photos.length}{" "}
            photo{row.photos.length > 1 ? "s" : ""}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        data-testid={`quick-photo-take-${row.id}`}
        className={`shrink-0 flex items-center gap-2 border-2 border-black rounded-lg py-2 px-3 font-bold text-sm ${
          missing
            ? "bg-[#FFF7D6] hover:bg-[#FFE9A6]"
            : "bg-white hover:bg-[#DFEEF7]"
        } disabled:opacity-60`}
      >
        <Camera size={16} strokeWidth={2.5} />
        {uploading ? "…" : missing ? "Take" : "Replace"}
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        data-testid={`quick-photo-input-${row.id}`}
        onChange={onFile}
      />
    </div>
  );
};

const QuickPhotos = () => {
  const [params] = useSearchParams();
  // Deep-link from the Dashboard Photo Coverage rings: `?kind=stock`
  // (or machinery / assets) preselects the tab, `?missing=1` (default)
  // keeps the "Missing only" toggle on so the manager lands on
  // exactly the items that need a photo.
  const initialKind = ["stock", "machinery", "assets"].includes(
    params.get("kind"),
  )
    ? params.get("kind")
    : "stock";
  const [kindId, setKindId] = useState(initialKind);
  const [rawRows, setRawRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [missingOnly, setMissingOnly] = useState(params.get("missing") !== "0");
  const [batch, setBatch] = useState(true);
  const [nextTargetId, setNextTargetId] = useState(null);

  const kind = KINDS.find((k) => k.id === kindId);

  // Users + departments only matter for the Assets tab (to resolve
  // assignee_id → staff name) but fetching once on mount keeps the tab
  // switch instant. Both are small collections.
  useEffect(() => {
    Promise.all([
      api.get("/users/public").catch(() => ({ data: [] })),
      api.get("/departments").catch(() => ({ data: [] })),
    ]).then(([{ data: u }, { data: d }]) => {
      setUsers(u || []);
      setDepartments(d || []);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get(kind.listUrl)
      .then((r) => setRawRows(r.data || []))
      .catch(() => toast.error(`Couldn't load ${kind.label.toLowerCase()}`))
      .finally(() => setLoading(false));
    setNextTargetId(null);
  }, [kindId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Normalise at render time so the Assets tab picks up assignee names
  // as soon as /users/public resolves (fetches happen in parallel).
  const rows = useMemo(
    () => rawRows.map((r) => kind.normalize(r, { users, departments })),
    [rawRows, kind, users, departments],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (missingOnly && r.photos.length > 0) return false;
      if (!q) return true;
      return (
        r.name.toLowerCase().includes(q) ||
        (r.subtitle || "").toLowerCase().includes(q) ||
        (r.meta?.brandModel || "").toLowerCase().includes(q) ||
        (r.meta?.location || "").toLowerCase().includes(q) ||
        (r.meta?.assignee || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, missingOnly]);

  const totals = useMemo(() => {
    const missing = rows.filter((r) => r.photos.length === 0).length;
    return {
      total: rows.length,
      missing,
      done: rows.length - missing,
    };
  }, [rows]);

  const onSaved = (updated) => {
    // Merge the new photo list back into the raw row so the memo
    // re-runs and the Missing badge flips to Done.
    setRawRows((prev) =>
      prev.map((r) =>
        r.id === updated.id ? { ...r, image_paths: updated.photos } : r,
      ),
    );
    if (batch) {
      // Find the next photoless row after the one just saved.
      const idx = filtered.findIndex((r) => r.id === updated.id);
      const rest = filtered
        .slice(idx + 1)
        .concat(filtered.slice(0, idx))
        .filter((r) => r.id !== updated.id && r.photos.length === 0);
      setNextTargetId(rest[0]?.id || null);
    }
  };

  return (
    <div
      className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4"
      data-testid="quick-photos-page"
    >
      <header className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-[#607087] font-bold">
          Podlab / Footwork · Warehouse
        </div>
        <h1 className="font-display text-3xl sm:text-4xl leading-none">
          Quick Photos
        </h1>
        <p className="text-sm text-[#404040]">
          Walk around with your iPad. Tap <b>Take</b> on any item to snap a
          photo — it&apos;s resized and set as the main image straight away.
        </p>
      </header>

      {/* Kind tabs */}
      <div className="flex gap-2 flex-wrap" data-testid="quick-photos-tabs">
        {KINDS.map((k) => {
          const Icon = k.icon;
          const active = k.id === kindId;
          return (
            <button
              key={k.id}
              type="button"
              onClick={() => setKindId(k.id)}
              data-testid={`quick-photos-tab-${k.id}`}
              className={`flex items-center gap-2 border-2 border-black rounded-full py-2 px-4 font-bold text-sm ${
                active
                  ? "bg-[#001740] text-white"
                  : "bg-white hover:bg-[#DFEEF7]"
              }`}
            >
              <Icon size={16} strokeWidth={2.5} />
              {k.label}
            </button>
          );
        })}
      </div>

      {/* Stats + controls */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <div
          className="border-2 border-black rounded-xl p-3 bg-white text-center"
          data-testid="quick-photos-stat-total"
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#607087]">
            Total
          </div>
          <div className="font-display text-2xl">{totals.total}</div>
        </div>
        <div
          className="border-2 border-black rounded-xl p-3 bg-[#FCE4E4] text-center"
          data-testid="quick-photos-stat-missing"
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#8A1F1F]">
            No photo
          </div>
          <div className="font-display text-2xl text-[#8A1F1F]">
            {totals.missing}
          </div>
        </div>
        <div
          className="border-2 border-black rounded-xl p-3 bg-[#E4F0E6] text-center"
          data-testid="quick-photos-stat-done"
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#1F6B3A]">
            Done
          </div>
          <div className="font-display text-2xl text-[#1F6B3A]">
            {totals.done}
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search
            size={16}
            strokeWidth={2.5}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#607087]"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, staff, location, brand…"
            data-testid="quick-photos-search"
            className="w-full border-2 border-black rounded-lg py-2 pl-9 pr-3 text-sm font-medium bg-white"
          />
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setMissingOnly((v) => !v)}
            data-testid="quick-photos-missing-toggle"
            className={`flex items-center gap-2 border-2 border-black rounded-lg py-2 px-3 font-bold text-sm ${
              missingOnly
                ? "bg-[#8A1F1F] text-white"
                : "bg-white hover:bg-[#DFEEF7]"
            }`}
          >
            <Layers size={16} strokeWidth={2.5} />
            {missingOnly ? "Missing only" : "All items"}
          </button>
          <button
            type="button"
            onClick={() => setBatch((v) => !v)}
            data-testid="quick-photos-batch-toggle"
            className={`flex items-center gap-2 border-2 border-black rounded-lg py-2 px-3 font-bold text-sm ${
              batch
                ? "bg-[#228EBF] text-white"
                : "bg-white hover:bg-[#DFEEF7]"
            }`}
            title={
              batch
                ? "After saving, jump to the next item without a photo"
                : "Stay on the same row after saving"
            }
          >
            <Zap size={16} strokeWidth={2.5} />
            Batch
          </button>
        </div>
      </div>

      {/* Rows */}
      {loading ? (
        <div className="text-center py-12 text-[#607087]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div
          className="text-center py-12 text-[#607087] border-2 border-dashed border-[#607087] rounded-xl"
          data-testid="quick-photos-empty"
        >
          {missingOnly
            ? "Nice — everything in this list has a photo."
            : "Nothing matches your search."}
        </div>
      ) : (
        <div className="space-y-2" data-testid="quick-photos-list">
          {filtered.map((r) => (
            <QuickPhotoRow
              key={r.id}
              row={r}
              kind={kind}
              onSaved={onSaved}
              isNextTarget={batch && r.id === nextTargetId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default QuickPhotos;
